import L from 'leaflet';
import { getRouteColor } from '../../../styles/routeColors.js';

export const DEFAULT_CENTER = { lat: -31.547996, lng: -68.5214701 };
export const TASK_MARKER_RING_STYLES = {
  default: {
    ringBackground: 'rgba(226,232,240,0.55)'
  },
  pinned: {
    ringBackground: '#facc15'
  },
  late: {
    ringBackground: '#f43f5e'
  },
  pinnedLate: {
    ringBackground: 'conic-gradient(from 90deg, #facc15 0deg 180deg, #f43f5e 180deg 360deg)'
  }
};
export const TASK_MARKER_RING_LEGEND = [
  {
    key: 'default',
    label: 'Sin alerta',
    description: 'Tarea normal, sin fijacion ni demora.',
    ringBackground: TASK_MARKER_RING_STYLES.default.ringBackground
  },
  {
    key: 'pinned',
    label: 'Pre-asignada/fijada',
    description: 'La tarea ya venía asociada a ese equipo antes de esta ejecución.',
    ringBackground: TASK_MARKER_RING_STYLES.pinned.ringBackground
  },
  {
    key: 'late',
    label: 'Llega tarde',
    description: 'La llegada estimada queda despues del horario esperado.',
    ringBackground: TASK_MARKER_RING_STYLES.late.ringBackground
  },
  {
    key: 'pinnedLate',
    label: 'Asociada y llega tarde',
    description: 'Combina asociacion previa y demora en la llegada.',
    ringBackground: TASK_MARKER_RING_STYLES.pinnedLate.ringBackground
  }
];
const MARKER_OVERLAP_DISTANCE_METERS = 18;
const MARKER_SPREAD_RADIUS_METERS = 24;

export const FIXED_ROUTING_PRIORITY_PRESET = {
  label: 'Priorizar puntualidad',
  windowToleranceMinutes: 30,
  windowPenaltyPerMinute: 1000,
  description: 'El enrutado prioriza llegar cerca del horario programado dentro de cada zona.'
};

export const OPTIMIZATION_CRITERIA = [
  {
    value: 'punctuality',
    label: 'Puntualidad',
    windowToleranceMinutes: 30,
    description: 'Prioriza respetar los horarios programados de las tareas.'
  },
  {
    value: 'balanced',
    label: 'Equilibrado',
    windowToleranceMinutes: 60,
    description: 'Busca un equilibrio entre cumplimiento horario y reducción de traslados.'
  },
  {
    value: 'efficiency',
    label: 'Eficiencia del recorrido',
    windowToleranceMinutes: 120,
    description: 'Prioriza agrupar tareas cercanas y reducir traslados, pudiendo postergar tareas anteriores cuando convenga.'
  }
];

export const getOptimizationCriterion = (value) => (
  OPTIMIZATION_CRITERIA.find((criterion) => criterion.value === value) || OPTIMIZATION_CRITERIA[1]
);

const SIMULATION_PRIORITY_ORDER = {
  alta: 0,
  media: 1,
  baja: 2
};

export function formatDateInput(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatMinutes(totalMinutes) {
  const safeMinutes = Math.max(0, Number(totalMinutes || 0));
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;
  return `${hours}h ${String(minutes).padStart(2, '0')}m`;
}

export function formatDistanceKm(totalKm) {
  const safeKm = Math.max(0, Number(totalKm || 0));
  const roundedKm = Math.round(safeKm * 10) / 10;
  const hasDecimals = roundedKm % 1 !== 0;

  return `${roundedKm.toLocaleString('es-AR', {
    minimumFractionDigits: hasDecimals ? 1 : 0,
    maximumFractionDigits: 1
  })} km`;
}

export function formatPercentage(value, total) {
  if (!total) {
    return 0;
  }

  return Math.min(100, Math.round((Number(value || 0) / Number(total || 1)) * 100));
}

export function formatTaskDateTime(value) {
  if (!value) {
    return 'Sin horario';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Sin horario';
  }

  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

export function normalizeTaskTeamId(teamId) {
  if (teamId == null) {
    return '';
  }

  const normalizedValue = String(teamId).trim();
  if (!normalizedValue) {
    return '';
  }

  const lowerCasedValue = normalizedValue.toLowerCase();
  if (lowerCasedValue === 'null' || lowerCasedValue === 'undefined') {
    return '';
  }

  return normalizedValue;
}

export function hasAssignedTeam(task) {
  return Boolean(normalizeTaskTeamId(task?.teamId ?? task?.team?.id ?? task?.preassignedTeamId));
}

export function isFixedTeamTask(task) {
  return hasAssignedTeam(task) && task?.fixedTeam === true;
}

export function isPreassignedTask(task) {
  return hasAssignedTeam(task) && task?.teamId != null && task?.fixedTeam !== true;
}

export function getTaskAssignmentLabel(task) {
  if (isFixedTeamTask(task)) {
    return 'Fijada';
  }

  if (isPreassignedTask(task)) {
    return 'Pre-asignada';
  }

  return '';
}

function hasOwnField(object, fieldName) {
  return Boolean(object) && Object.prototype.hasOwnProperty.call(object, fieldName);
}

export function resolveStopAssignmentState(stop = {}, sourceTask = null) {
  const hasExplicitFixedState = hasOwnField(stop, 'isFixed');
  const hasExplicitPreassignedState = hasOwnField(stop, 'isPreassigned');
  const isFixed = hasExplicitFixedState
    ? stop.isFixed === true
    : isFixedTeamTask(sourceTask);
  const isPreassigned = hasExplicitPreassignedState
    ? stop.isPreassigned === true
    : (!isFixed && isPreassignedTask(sourceTask));

  return {
    isFixed,
    isPreassigned,
    isPinned: isFixed || isPreassigned
  };
}

export function getStopAssignmentLabel(stop = {}, sourceTask = null) {
  const { isFixed, isPreassigned } = resolveStopAssignmentState(stop, sourceTask);

  if (isFixed) {
    return 'Fijada';
  }

  if (isPreassigned) {
    return 'Pre-asignada';
  }

  return '';
}

export function hasPinnedTeam(task) {
  return hasAssignedTeam(task);
}

export function formatDeviationLabel(value) {
  if (!Number.isFinite(Number(value))) {
    return 'Sin horario programado';
  }

  const numericValue = Number(value);
  if (numericValue === 0) {
    return 'En horario';
  }

  return `${Math.abs(numericValue)} min ${numericValue > 0 ? 'tarde' : 'temprano'}`;
}

export function buildMarkerIcon(label, color, options = {}) {
  const { isBase = false, isRouteOrigin = false, ringVariant = 'default' } = options;
  const ringBackground = isBase
    ? 'rgba(148,163,184,0.7)'
    : (TASK_MARKER_RING_STYLES[ringVariant] || TASK_MARKER_RING_STYLES.default).ringBackground;
  const outerSize = isRouteOrigin ? 18 : (isBase ? 36 : 38);
  const innerSize = isRouteOrigin ? 12 : (isBase ? 30 : 32);
  const outerPadding = (outerSize - innerSize) / 2;
  const outerShadow = isBase || isRouteOrigin ? 'none' : '0 10px 24px rgba(0,0,0,0.28)';

  return L.divIcon({
    className: 'task-distribution-marker',
    html: `
      <div style="
        width:${outerSize}px;
        height:${outerSize}px;
        border-radius:9999px;
        display:flex;
        align-items:center;
        justify-content:center;
        padding:${outerPadding}px;
        background:${ringBackground};
        box-shadow:${outerShadow};
      ">
        <div style="
          width:${innerSize}px;
          height:${innerSize}px;
          border-radius:9999px;
          border:2px solid ${isBase ? '#000000' : 'rgba(255,255,255,0.92)'};
          background:${isBase ? '#ffffff' : color};
          color:${isBase ? '#000000' : '#ffffff'};
          display:flex;
          align-items:center;
          justify-content:center;
          font-size:${isBase ? 11 : 12}px;
          font-weight:700;
          line-height:1;
        ">${label}</div>
      </div>
    `,
    iconSize: [outerSize, outerSize],
    iconAnchor: [outerSize / 2, outerSize / 2]
  });
}

function getTaskMarkerRingVariant(stop, sourceTask = null, isBase = false, windowToleranceMinutes = 60) {
  if (isBase) {
    return 'default';
  }

  const { isPinned } = resolveStopAssignmentState(stop, sourceTask);
  const deviationMinutes = Number(stop?.deviationMinutes);
  const isOutsideWindow = typeof stop?.withinWindow === 'boolean'
    ? !stop.withinWindow
    : Number.isFinite(deviationMinutes) && Math.abs(deviationMinutes) > windowToleranceMinutes;

  if (isPinned && isOutsideWindow) {
    return 'pinnedLate';
  }

  if (isOutsideWindow) {
    return 'late';
  }

  if (isPinned) {
    return 'pinned';
  }

  return 'default';
}

function hasValidCoordinates(lat, lng) {
  return Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));
}

function toFiniteNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function buildCentroidPoint(points = [], fallbackPoint) {
  if (!points.length) {
    return fallbackPoint;
  }

  const totals = points.reduce((accumulator, point) => ({
    lat: accumulator.lat + point.lat,
    lng: accumulator.lng + point.lng
  }), { lat: 0, lng: 0 });

  return {
    lat: totals.lat / points.length,
    lng: totals.lng / points.length
  };
}

function computeSimulationAngle(point, basePoint) {
  const angle = Math.atan2(point.lat - basePoint.lat, point.lng - basePoint.lng);
  return angle >= 0 ? angle : angle + (Math.PI * 2);
}

function resolveSimulationTaskId(task = {}) {
  const id = task.id ?? task.taskId ?? null;
  return id == null ? '' : String(id).trim();
}

function resolveSimulationTaskTitle(task = {}) {
  return String(task.title || task.name || 'Tarea sin titulo').trim() || 'Tarea sin titulo';
}

function resolveSimulationTaskPriority(task = {}) {
  const rawPriority = task.priority ?? task.taskPriority ?? task.TaskType?.priority ?? task.taskType?.priority ?? 'media';
  const normalizedPriority = String(rawPriority || 'media').toLowerCase();
  return SIMULATION_PRIORITY_ORDER[normalizedPriority] != null ? normalizedPriority : 'media';
}

function resolveSimulationTaskWeight(task = {}) {
  const candidateValues = [
    task.weight,
    task.estimatedDurationMinutes,
    task.estimatedDuration,
    task.TaskType?.estimatedDuration,
    task.taskType?.estimatedDuration
  ];

  for (const value of candidateValues) {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue) && numericValue > 0) {
      return numericValue;
    }
  }

  return 30;
}

function resolveSimulationScheduledTimestamp(task = {}) {
  const rawValue = task.scheduledDate ?? task.scheduledAt ?? null;
  if (!rawValue) {
    return null;
  }

  const parsedDate = new Date(rawValue);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.getTime();
}

function compareSimulationTasks(left, right) {
  const leftHasSchedule = Number.isFinite(left.scheduledTimestamp);
  const rightHasSchedule = Number.isFinite(right.scheduledTimestamp);

  if (leftHasSchedule !== rightHasSchedule) {
    return leftHasSchedule ? -1 : 1;
  }

  if (leftHasSchedule && rightHasSchedule && left.scheduledTimestamp !== right.scheduledTimestamp) {
    return left.scheduledTimestamp - right.scheduledTimestamp;
  }

  const leftPriority = SIMULATION_PRIORITY_ORDER[left.priority] ?? SIMULATION_PRIORITY_ORDER.media;
  const rightPriority = SIMULATION_PRIORITY_ORDER[right.priority] ?? SIMULATION_PRIORITY_ORDER.media;

  if (leftPriority !== rightPriority) {
    return leftPriority - rightPriority;
  }

  if (right.weight !== left.weight) {
    return right.weight - left.weight;
  }

  return String(left.id).localeCompare(String(right.id));
}

function pickSeedTaskForSimulation(sortedTasks, targetPosition, usedTaskIds) {
  if (!sortedTasks.length) {
    return null;
  }

  const normalizedTarget = Math.max(0, Math.min(sortedTasks.length - 1, targetPosition));

  for (let offset = 0; offset < sortedTasks.length; offset += 1) {
    const rightIndex = (normalizedTarget + offset) % sortedTasks.length;
    const rightTask = sortedTasks[rightIndex];
    if (!usedTaskIds.has(rightTask.id)) {
      return rightTask;
    }

    if (offset === 0) {
      continue;
    }

    const leftIndex = (normalizedTarget - offset + sortedTasks.length) % sortedTasks.length;
    const leftTask = sortedTasks[leftIndex];
    if (!usedTaskIds.has(leftTask.id)) {
      return leftTask;
    }
  }

  return null;
}

export function resolveTaskCoordinates(task = {}) {
  const coordinateCandidates = [
    [task.lat, task.lng],
    [task.clientLat, task.clientLng],
    [task.client?.lat, task.client?.lng],
    [task.clientSnapshot?.lat, task.clientSnapshot?.lng]
  ];

  for (const [latValue, lngValue] of coordinateCandidates) {
    const lat = toFiniteNumber(latValue);
    const lng = toFiniteNumber(lngValue);
    if (lat != null && lng != null) {
      return { lat, lng };
    }
  }

  return null;
}

export function buildZoneSimulationData({ tasks = [], distributionResult = null } = {}) {
  const assignments = Array.isArray(distributionResult?.assignments) ? distributionResult.assignments : [];
  if (!assignments.length) {
    return null;
  }

  const fallbackBasePoint = { lat: DEFAULT_CENTER.lat, lng: DEFAULT_CENTER.lng };
  const baseStop = assignments
    .flatMap((assignment) => assignment.stops || [])
    .find((stop) => stop.type === 'BASE_START' && hasValidCoordinates(stop.lat, stop.lng));
  const basePoint = baseStop
    ? { lat: Number(baseStop.lat), lng: Number(baseStop.lng) }
    : fallbackBasePoint;

  const teams = assignments.map((assignment, index) => ({
    teamId: String(assignment.teamId),
    teamName: assignment.teamName || 'Equipo sin nombre',
    color: assignment.color || getRouteColor(index)
  }));

  const sourceTasksById = new Map();

  const registerTask = (rawTask = {}, extraFields = {}) => {
    const taskId = resolveSimulationTaskId(rawTask);
    if (!taskId) {
      return;
    }

    const coordinates = resolveTaskCoordinates({ ...rawTask, ...extraFields });
    if (!coordinates) {
      return;
    }

    const previousTask = sourceTasksById.get(taskId) || {};
    const assignedTeamId = normalizeTaskTeamId(
      rawTask.teamId
      ?? rawTask.team?.id
    );
    const preassignedTeamId = normalizeTaskTeamId(
      extraFields.preassignedTeamId
      ?? rawTask.preassignedTeamId
      ?? assignedTeamId
    );
    const hasAssignedTeam = Boolean(assignedTeamId || preassignedTeamId);
    const isPinned = extraFields.isPreassigned != null
      ? Boolean(extraFields.isPreassigned)
      : (
          rawTask.isPreassigned != null
            ? Boolean(rawTask.isPreassigned)
            : (
                extraFields.isFixed != null
                  ? Boolean(extraFields.isFixed)
                  : Boolean((rawTask.isFixed || rawTask.fixedTeam === true) && hasAssignedTeam)
              )
        );

    sourceTasksById.set(taskId, {
      ...previousTask,
      id: taskId,
      title: resolveSimulationTaskTitle(rawTask),
      lat: coordinates.lat,
      lng: coordinates.lng,
      priority: resolveSimulationTaskPriority(rawTask),
      weight: resolveSimulationTaskWeight(rawTask),
      scheduledTimestamp: resolveSimulationScheduledTimestamp(rawTask),
      isPreassigned: isPinned,
      preassignedTeamId: preassignedTeamId || previousTask.preassignedTeamId || '',
      finalTeamId: extraFields.finalTeamId || previousTask.finalTeamId || null
    });
  };

  tasks.forEach((task) => {
    registerTask(task);
  });

  assignments.forEach((assignment, assignmentIndex) => {
    const teamId = String(assignment.teamId);
    const teamColor = assignment.color || getRouteColor(assignmentIndex);

    (assignment.stops || [])
      .filter((stop) => stop.type === 'TASK' && stop.taskId)
      .forEach((stop) => {
        registerTask(stop, {
          id: stop.taskId,
          title: stop.title,
          lat: stop.lat,
          lng: stop.lng,
          isPreassigned: Boolean(stop.isPreassigned || stop.isFixed),
          isFixed: Boolean(stop.isFixed),
          preassignedTeamId: (stop.isPreassigned || stop.isFixed) ? teamId : '',
          finalTeamId: teamId,
          color: teamColor
        });
      });
  });

  const taskList = [...sourceTasksById.values()]
    .filter((task) => hasValidCoordinates(task.lat, task.lng))
    .map((task) => ({
      ...task,
      angle: computeSimulationAngle(task, basePoint)
    }));

  if (!taskList.length) {
    return null;
  }

  const taskById = new Map(taskList.map((task) => [task.id, task]));
  const angleSortedTasks = [...taskList].sort((left, right) => {
    if (left.angle !== right.angle) {
      return left.angle - right.angle;
    }

    return left.id.localeCompare(right.id);
  });

  const teamTaskIds = new Map(teams.map((team) => [team.teamId, []]));
  const teamSeedCenters = new Map();
  const seedSteps = [];

  teams.forEach((team) => {
    const pinnedTasks = taskList.filter((task) => (
      task.isPreassigned && String(task.preassignedTeamId) === String(team.teamId)
    ));

    if (!pinnedTasks.length) {
      return;
    }

    pinnedTasks.forEach((task) => {
      teamTaskIds.get(team.teamId)?.push(task.id);
      seedSteps.push({
        kind: 'seed',
        title: 'Ancla fija',
        description: `${team.teamName} ya arranca con una tarea fijada que actua como ancla de zona.`,
        taskId: task.id,
        teamId: team.teamId,
        startPosition: [basePoint.lat, basePoint.lng],
        endPosition: [task.lat, task.lng]
      });
    });

    const pinnedPoints = pinnedTasks.map((task) => ({ lat: task.lat, lng: task.lng }));
    teamSeedCenters.set(team.teamId, buildCentroidPoint(pinnedPoints, basePoint));
  });

  const teamsWithoutSeed = teams.filter((team) => !teamSeedCenters.has(team.teamId));
  const candidateSeedTasks = angleSortedTasks.filter((task) => !task.isPreassigned);
  const usedSeedTaskIds = new Set(seedSteps.map((step) => step.taskId));

  teamsWithoutSeed.forEach((team, index) => {
    const targetPosition = candidateSeedTasks.length
      ? Math.floor((candidateSeedTasks.length * index) / teamsWithoutSeed.length)
      : 0;
    const seedTask = pickSeedTaskForSimulation(candidateSeedTasks, targetPosition, usedSeedTaskIds);

    if (!seedTask) {
      teamSeedCenters.set(team.teamId, basePoint);
      return;
    }

    usedSeedTaskIds.add(seedTask.id);
    teamTaskIds.get(team.teamId)?.push(seedTask.id);
    teamSeedCenters.set(team.teamId, { lat: seedTask.lat, lng: seedTask.lng });
    seedSteps.push({
      kind: 'seed',
      title: 'Semilla angular',
      description: `${team.teamName} toma una tarea de referencia para arrancar su zona desde este sector.`,
      taskId: seedTask.id,
      teamId: team.teamId,
      startPosition: [basePoint.lat, basePoint.lng],
      endPosition: [seedTask.lat, seedTask.lng]
    });
  });

  const seedTaskIdSet = new Set(seedSteps.map((step) => step.taskId));
  const remainingTasks = taskList
    .filter((task) => !seedTaskIdSet.has(task.id))
    .sort(compareSimulationTasks);

  const assignmentSteps = [];

  remainingTasks.forEach((task) => {
    const assignedTeamId = task.finalTeamId ? String(task.finalTeamId) : '';
    if (!assignedTeamId || !teamTaskIds.has(assignedTeamId)) {
      return;
    }

    const currentTeamTaskIds = teamTaskIds.get(assignedTeamId) || [];
    const currentCenterPoint = currentTeamTaskIds.length
      ? buildCentroidPoint(
          currentTeamTaskIds
            .map((taskId) => taskById.get(taskId))
            .filter(Boolean)
            .map((teamTask) => ({ lat: teamTask.lat, lng: teamTask.lng })),
          teamSeedCenters.get(assignedTeamId) || basePoint
        )
      : (teamSeedCenters.get(assignedTeamId) || basePoint);

    currentTeamTaskIds.push(task.id);
    teamTaskIds.set(assignedTeamId, currentTeamTaskIds);
    teamSeedCenters.set(
      assignedTeamId,
      buildCentroidPoint(
        currentTeamTaskIds
          .map((taskId) => taskById.get(taskId))
          .filter(Boolean)
          .map((teamTask) => ({ lat: teamTask.lat, lng: teamTask.lng })),
        basePoint
      )
    );

    const assignedTeam = teams.find((team) => team.teamId === assignedTeamId);
    assignmentSteps.push({
      kind: 'assign',
      title: 'Crecimiento de zona',
      description: `${task.title} se suma a ${assignedTeam?.teamName || 'su equipo'} porque su zona actual queda mas cerca y sigue siendo viable.`,
      taskId: task.id,
      teamId: assignedTeamId,
      startPosition: [currentCenterPoint.lat, currentCenterPoint.lng],
      endPosition: [task.lat, task.lng]
    });
  });

  const bounds = [
    [basePoint.lat, basePoint.lng],
    ...taskList.map((task) => [task.lat, task.lng])
  ];

  return {
    basePosition: [basePoint.lat, basePoint.lng],
    bounds,
    teams,
    tasks: taskList,
    unassignedTaskIds: taskList
      .filter((task) => !task.finalTeamId)
      .map((task) => task.id),
    steps: [
      {
        kind: 'intro',
        title: 'Tareas visibles',
        description: 'Primero aparecen todas las tareas seleccionadas alrededor de la base.'
      },
      ...angleSortedTasks.map((task) => ({
        kind: 'angle',
        title: 'Lectura angular',
        description: `Se mide el angulo de ${task.title} respecto de la base para ubicarla dentro del circulo.`,
        taskId: task.id,
        startPosition: [basePoint.lat, basePoint.lng],
        endPosition: [task.lat, task.lng]
      })),
      ...seedSteps,
      ...assignmentSteps,
      {
        kind: 'complete',
        title: 'Rutas finales',
        description: 'Con las zonas ya armadas, el sistema enruta cada equipo dentro de su propia zona.'
      }
    ]
  };
}

function offsetPosition([lat, lng], angle, distanceMeters) {
  const latOffset = (Math.sin(angle) * distanceMeters) / 111320;
  const cosLatitude = Math.cos((lat * Math.PI) / 180);
  const lngOffset = cosLatitude === 0
    ? 0
    : (Math.cos(angle) * distanceMeters) / (111320 * cosLatitude);

  return [lat + latOffset, lng + lngOffset];
}

function spreadOverlappingMarkers(markers) {
  const groups = [];
  const visited = new Set();

  const distanceBetween = (leftMarker, rightMarker) => (
    L.latLng(leftMarker.originalPosition[0], leftMarker.originalPosition[1])
      .distanceTo(L.latLng(rightMarker.originalPosition[0], rightMarker.originalPosition[1]))
  );

  for (let index = 0; index < markers.length; index += 1) {
    if (visited.has(index)) {
      continue;
    }

    const groupIndexes = [index];
    visited.add(index);

    for (let cursor = 0; cursor < groupIndexes.length; cursor += 1) {
      const currentIndex = groupIndexes[cursor];

      for (let candidateIndex = 0; candidateIndex < markers.length; candidateIndex += 1) {
        if (visited.has(candidateIndex)) {
          continue;
        }

        if (distanceBetween(markers[currentIndex], markers[candidateIndex]) <= MARKER_OVERLAP_DISTANCE_METERS) {
          visited.add(candidateIndex);
          groupIndexes.push(candidateIndex);
        }
      }
    }

    groups.push(groupIndexes.map((groupIndex) => markers[groupIndex]));
  }

  const spreadMarkers = [];
  const overlappingGroups = [];

  groups.forEach((group) => {
    if (group.length === 1) {
      spreadMarkers.push(group[0]);
      return;
    }

    const baseMarkers = group.filter((marker) => marker.isBase);
    const taskMarkers = group.filter((marker) => !marker.isBase);
    const center = group[0].originalPosition;

    baseMarkers.forEach((marker) => {
      spreadMarkers.push({
        ...marker,
        position: marker.originalPosition
      });
    });

    if (!taskMarkers.length) {
      return;
    }

    const radius = Math.max(MARKER_SPREAD_RADIUS_METERS, 14 + taskMarkers.length * 4);
    taskMarkers.forEach((marker, markerIndex) => {
      const angle = (-Math.PI / 2) + ((Math.PI * 2 * markerIndex) / taskMarkers.length);
      spreadMarkers.push({
        ...marker,
        position: offsetPosition(center, angle, radius)
      });
    });

    overlappingGroups.push({
      center,
      count: group.length,
      labels: group.map((marker) => (
        `${marker.teamName} ${marker.isBase ? 'Base' : (marker.isRouteOrigin ? 'Ultima tarea atendida' : `#${marker.label}`)}`
      ))
    });
  });

  return {
    markers: spreadMarkers,
    overlappingGroups
  };
}

export function countTaskStops(assignment) {
  return (assignment?.stops || []).filter((stop) => stop.type === 'TASK').length;
}

export function buildMapArtifacts(result, taskById = new Map()) {
  if (!result) {
    return {
      markers: [],
      geoJsonRoutes: [],
      bounds: [],
      invalidStops: [],
      overlappingGroups: []
    };
  }

  const markers = [];
  const geoJsonRoutes = [];
  const bounds = [];
  const invalidStops = [];
  const optimizationCriterion = getOptimizationCriterion(
    result.metadata?.appliedConfiguration?.optimizationCriterion
  );
  const windowToleranceMinutes = optimizationCriterion.windowToleranceMinutes;

  for (const [assignmentIndex, assignment] of (result.assignments || []).entries()) {
    const color = assignment.color || getRouteColor(assignmentIndex);
    let nextTaskNumber = 0;
    if (assignment.route?.geometry) {
      geoJsonRoutes.push({
        id: `${assignment.teamId}-route`,
        teamId: assignment.teamId,
        teamName: assignment.teamName || 'Equipo sin nombre',
        color,
        geometry: assignment.route.geometry
      });

      const coordinates = assignment.route.geometry.coordinates || [];
      coordinates.forEach(([lng, lat]) => bounds.push([lat, lng]));
    }

    (assignment.stops || []).forEach((stop) => {
      const isBase = stop.type === 'BASE_START' || stop.type === 'BASE_END';
      const isRouteOrigin = stop.type === 'ROUTE_ORIGIN' || stop.status === 'finalizada';
      if (!isBase && !isRouteOrigin) {
        nextTaskNumber += 1;
      }
      const sourceTask = taskById.get(String(stop.taskId || ''));
      const ringVariant = getTaskMarkerRingVariant(stop, sourceTask, isBase, windowToleranceMinutes);
      const assignmentLabel = getStopAssignmentLabel(stop, sourceTask);

      if (!hasValidCoordinates(stop.lat, stop.lng)) {
        if (!isBase) {
          invalidStops.push({
            teamName: assignment.teamName || 'Equipo sin nombre',
            order: stop.order,
            title: stop.title || 'Tarea sin titulo'
          });
        }
        return;
      }

      bounds.push([Number(stop.lat), Number(stop.lng)]);

      const label = isBase ? 'B' : (isRouteOrigin ? '' : String(nextTaskNumber));
      markers.push({
        id: `${assignment.teamId}-${stop.order}-${stop.type}`,
        position: [Number(stop.lat), Number(stop.lng)],
        originalPosition: [Number(stop.lat), Number(stop.lng)],
        icon: buildMarkerIcon(label, color, {
          isBase,
          isRouteOrigin,
          ringVariant
        }),
        isBase,
        isRouteOrigin,
        label,
        teamId: assignment.teamId,
        teamName: assignment.teamName || 'Equipo sin nombre',
        ringVariant,
        popup: (
          <div className="space-y-1 text-sm text-ink">
            <div className="font-semibold">{isBase ? stop.name : stop.title}</div>
            {!isBase && stop.category && <div>Categoria: {stop.category}</div>}
            {!isBase && stop.address && <div>{stop.address}</div>}
            {!isBase && assignmentLabel && <div>{assignmentLabel} al equipo</div>}
            {!isBase && stop.scheduledTime && (
              <div>Prog. {stop.scheduledTime} - llegada {stop.estimatedArrivalTime}</div>
            )}
          </div>
        )
      });
    });
  }

  const spreadArtifacts = spreadOverlappingMarkers(markers);

  return {
    markers: spreadArtifacts.markers,
    geoJsonRoutes,
    bounds,
    invalidStops,
    overlappingGroups: spreadArtifacts.overlappingGroups
  };
}
